import { View, Text, styled, ScrollView } from 'tamagui'
import { Mic, Headphones, Shield } from '@tamagui/lucide-icons'
import { useState } from 'react'

const Container = styled(View, { flex: 1, flexDirection: 'column' })
const Header = styled(View, { paddingHorizontal: 32, paddingVertical: 20, borderBottomWidth: 1, borderBottomColor: '$borderDefault' })
const Title = styled(Text, { fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground' })
const Body = styled(View, { padding: 32, gap: 24, maxWidth: 600 })
const Section = styled(View, { gap: 12 })
const SectionTitle = styled(Text, { fontFamily: '$fontBody', fontWeight: '600', fontSize: 16, color: '$surfaceForeground' })

const DeviceRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
  paddingVertical: 10, paddingHorizontal: 12, borderRadius: '$borderRadiusLg',
  borderWidth: 1, borderColor: '$borderDefault',
})

const VolumeBar = styled(View, {
  height: 6, borderRadius: '$borderRadiusSm', backgroundColor: '$sidebarActive', flex: 1,
})

const VolumeLevel = styled(View, {
  height: 6, borderRadius: '$borderRadiusSm', backgroundColor: '$accent',
})

const E2eBadge = styled(View, {
  flexDirection: 'row', alignItems: 'center', gap: 8,
  backgroundColor: '$accentLighter', borderRadius: '$borderRadiusLg', padding: 12,
  borderWidth: 1, borderColor: '$accent',
})

const Toggle = styled(View, {
  width: 40, height: 22, borderRadius: '$borderRadiusXl', cursor: 'pointer', justifyContent: 'center',
  variants: { active: { true: { backgroundColor: '$accent' }, false: { backgroundColor: '$sidebarActive' } } } as const,
})

const ToggleKnob = styled(View, {
  width: 18, height: 18, borderRadius: '$borderRadiusLg', backgroundColor: '$surfaceForeground',
  // @ts-expect-error -- web transition
  style: { transition: 'margin-left 150ms ease' },
})

export function VoiceVideoSettings() {
  const [noiseSuppression, setNoiseSuppression] = useState(true)
  const [echoCancellation, setEchoCancellation] = useState(true)
  const [spatialAudio, setSpatialAudio] = useState(true)

  return (
    <Container>
      <Header><Title>Voice & Video</Title></Header>
      <ScrollView flex={1}>
        <Body>
          <E2eBadge>
            <Shield size={18} color="$accent" />
            <View flex={1}>
              <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surfaceForeground">
                Voice calls are end-to-end encrypted
              </Text>
              <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">
                Powered by Cloudflare Realtime SFU with client-side encryption
              </Text>
            </View>
          </E2eBadge>

          <Section>
            <SectionTitle>Input Device</SectionTitle>
            <DeviceRow>
              <View flexDirection="row" alignItems="center" gap={8}>
                <Mic size={16} color="$sidebarMuted" />
                <Text fontFamily="$fontBody" fontSize={14} color="$surfaceForeground">Default Microphone</Text>
              </View>
            </DeviceRow>
            <View gap={4}>
              <Text fontFamily="$fontBody" fontSize={12} color="$color8">Input Volume</Text>
              <VolumeBar><VolumeLevel width="72%" /></VolumeBar>
            </View>
          </Section>

          <Section>
            <SectionTitle>Output Device</SectionTitle>
            <DeviceRow>
              <View flexDirection="row" alignItems="center" gap={8}>
                <Headphones size={16} color="$sidebarMuted" />
                <Text fontFamily="$fontBody" fontSize={14} color="$surfaceForeground">Default Speakers</Text>
              </View>
            </DeviceRow>
            <View gap={4}>
              <Text fontFamily="$fontBody" fontSize={12} color="$color8">Output Volume</Text>
              <VolumeBar><VolumeLevel width="85%" /></VolumeBar>
            </View>
          </Section>

          <Section>
            <SectionTitle>Processing</SectionTitle>
            {([
              { label: 'Noise Suppression', desc: 'Reduce background noise', value: noiseSuppression, toggle: setNoiseSuppression },
              { label: 'Echo Cancellation', desc: 'Prevent audio feedback', value: echoCancellation, toggle: setEchoCancellation },
              { label: 'Spatial Audio', desc: 'HRTF-based 3D positioning in voice channels', value: spatialAudio, toggle: setSpatialAudio },
            ]).map((setting) => (
              <DeviceRow key={setting.label}>
                <View flex={1} gap={2}>
                  <Text fontFamily="$fontBody" fontSize={14} fontWeight="500" color="$surfaceForeground">{setting.label}</Text>
                  <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">{setting.desc}</Text>
                </View>
                <Toggle active={setting.value} onPress={() => setting.toggle(!setting.value)}>
                  <ToggleKnob marginLeft={setting.value ? 20 : 2} />
                </Toggle>
              </DeviceRow>
            ))}
          </Section>
        </Body>
      </ScrollView>
    </Container>
  )
}
